import { cloneElement, isValidElement, ReactElement, ReactNode, useMemo, useState } from 'react'

import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react'

import { mergeRefs } from '../../utils/mergeRefs'
import { SMedium } from '../Typography'
import * as S from './FloatingTooltipCard.styles'

export interface FloatingTooltipCardProps {
  children: ReactNode
  content: ReactNode | (() => ReactNode)
  isOpen?: boolean
  title: ReactNode
}

export const FloatingTooltipCard = ({ children, content, title }: FloatingTooltipCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const { context, refs, floatingStyles } = useFloating<HTMLElement>({
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    middleware: [offset(4)],
    placement: 'bottom-end',
  })

  const dismiss = useDismiss(context)
  const hover = useHover(context, {
    restMs: 50,
    handleClose: safePolygon(),
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, hover])

  const resolvedContent = useMemo(() => {
    return typeof content === 'function' ? (content as () => ReactNode)() : content
  }, [content])

  const renderTrigger = () => {
    if (isValidElement(children)) {
      const child = children as ReactElement<unknown>
      const childProps = getReferenceProps(child.props ? { ...child.props } : {})

      // @ts-ignore
      const mergedRef = mergeRefs(child.ref, refs.setReference)
      // @ts-ignore
      return cloneElement(child, { ...childProps, ref: mergedRef })
    }
    // Fallback for non-element children: keep a minimal wrapper
    return (
      <S.TriggerWrapper
        ref={refs.setReference as unknown as (node: HTMLElement | null) => void}
        {...getReferenceProps()}
      >
        {children}
      </S.TriggerWrapper>
    )
  }

  return (
    <>
      {renderTrigger()}
      {isMenuOpen && resolvedContent ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <S.CardWrapper ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <S.TitleContainer>
                <SMedium>{title}</SMedium>
              </S.TitleContainer>
              <S.ContentWrapper>{resolvedContent}</S.ContentWrapper>
            </S.CardWrapper>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  )
}
